// src/pages/InventoryControlCenter.js
import React from 'react';
import $ from 'jquery';
import {
  Box, Grid, Paper, Stack, Button, TextField, Checkbox, FormControlLabel, Snackbar, Alert, AlertTitle, 
  Chip, Autocomplete, Table, TableHead, TableRow, TableCell, TableBody, Divider, Tooltip, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ClearIcon from '@mui/icons-material/Clear';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
// import SyncIcon from '@mui/icons-material/Sync';
import Client from '../Client';
import Config from '../ClientConfig';

const API_BASE = (Config.API_URL_REPORTS_MONTH_CLOSE || '/api/reports/month-close/');
const API = {
  OVERVIEW: API_BASE + 'locations_last_close/',
  OPENING_PRECHECK: API_BASE + 'opening_close_precheck/',
  IMPORT_OPENING_CLOSE: API_BASE + 'import_opening_close/',
  MONTH_CLOSE: API_BASE + 'month_close/',
  SALES_SYNC: API_BASE + 'sales_sync_consumption/',
  APPLY_CLOSE: API_BASE + 'apply_close_to_history/',
  IMPORT_INVENTORY_CSV: API_BASE + 'import_inventory_csv/',
  IMPORT_MOVEMENTS_CSV: API_BASE + 'import_movements_csv/',
  IMPORT_RECEIPTS_CSV: API_BASE + 'import_receipts_csv/',
  IMPORT_INTERNAL_USE_CSV: API_BASE + 'import_internal_use/',
  IMPORT_INTERNAL_MR_CSV: API_BASE + 'import_internal_material_receipt/',
  // Jobs endpoints (path params)
  JOBS: API_BASE + 'jobs/',
  JOB_DETAIL: API_BASE + 'job_detail/',   // use GET `${JOB_DETAIL}${id}/`
  JOB_CANCEL: API_BASE + 'job_cancel/',   // use POST `${JOB_CANCEL}${id}/`
};
let __iccDidMountOnce = false;
// const FORCE = '?inline_timeout=0';
// API.SALES_SYNC = API.SALES_SYNC + FORCE;
// API.IMPORT_RECEIPTS_CSV = API.IMPORT_RECEIPTS_CSV + FORCE;
// API.MONTH_CLOSE = API.MONTH_CLOSE + FORCE;
// API.IMPORT_MOVEMENTS_CSV = API.IMPORT_MOVEMENTS_CSV + FORCE;
// API.IMPORT_OPENING_CLOSE = API.IMPORT_OPENING_CLOSE + '?inline_timeout=0';
// API.IMPORT_INVENTORY_CSV = API.IMPORT_INVENTORY_CSV + '?inline_timeout=0';

/* =========================
   Small helpers / utilities
   ========================= */

// Run-once wrapper for client polyfills
function onceWrap(obj, methodName) {
  const orig = obj?.[methodName]?.bind(obj);
  if (!orig) return;
  let ran = false; let cached;
  obj[methodName] = function(...args) {
    if (ran) return cached;
    cached = orig(...args);
    ran = true;
    return cached;
  };
}
function safeStringify(obj) {
  try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
}
function firstDayOfMonth(yyyyMM) {
  if (!yyyyMM) return '';
  const [y, m] = yyyyMM.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toISOString().slice(0,10);
}
function lastDayOfMonth(yyyyMM) {
  if (!yyyyMM) return '';
  const [y, m] = yyyyMM.split('-').map(Number);
  const d = new Date(y, m, 0);
  return d.toISOString().slice(0,10);
}
function normalizeYYYYMM(val) {
  if (!val) return null;
  try {
    if (val?.slice) return String(val).slice(0, 7);
    if (val?.toISOString) return val.toISOString().slice(0, 7);
  } catch {}
  return null;
}
function inlineTimeoutForFile(file) {
  const size = file?.size || 0;
  if (size === 0) return 3;           // unknown or tiny → 3s try
  if (size <= 200 * 1024) return 3;   // ≤200KB → 3s
  if (size <= 2 * 1024 * 1024) return 2; // ≤2MB → 2s
  return 0;                           // big → background immediately
}
function computeBehindMonths(yyyyMM) {
  if (!yyyyMM) return null;
  const [y, m] = yyyyMM.split('-').map(n => parseInt(n, 10));
  if (!y || !m) return null;
  const now = new Date();
  const cur = now.getFullYear() * 12 + (now.getMonth() + 1);
  const val = y * 12 + m;
  return Math.max(0, cur - val);
}
function csvEscape(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function buildCsv(contentRows) {
  const csv = '\uFEFF' + contentRows.map(r => r.map(csvEscape).join(',')).join('\n') + '\n';
  return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const compactFieldSx = {
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};
const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};
const pageRootSx = {
  width: '100%',
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  bgcolor: 'background.default',
  p: {
    xs: 0.5,
    sm: 0.85,
    md: 1.1,
  },
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
};
const mainGridSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  alignContent: 'stretch',
};
const scrollPaperSx = {
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  p: {
    xs: 0.75,
    sm: 1,
  },
  borderRadius: '14px',
};
const tableScrollSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
};
const compactTableSx = {
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
  '& .MuiTableCell-root': {
    py: { xs: 0.45, sm: 0.6 },
    px: { xs: 0.5, sm: 0.75 },
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
  },
};
const commandPanelSx = {
  height: '100%',
  minHeight: 0,
  overflow: 'auto',
  p: {
    xs: 0.75,
    sm: 1,
  },
  borderRadius: '14px',
};
const logPaperSx = {
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  p: {
    xs: 0.5,
    sm: 0.6,
  },
  borderRadius: '14px',
};
const logBoxSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: {
    xs: 11.5,
    sm: 12.5,
  },
  lineHeight: 1.25,
  background: '#0b1020',
  color: '#e6eaff',
  p: 0.5,
  borderRadius: 1,
  textAlign: 'left',
};

// --- ANSI & log helpers for modal & log box ---
const ANSI_REGEX = /\x1b\[[0-9;]*m/g;
function stripAnsi(s='') { try { return String(s).replace(ANSI_REGEX, ''); } catch { return String(s || ''); } }
function toRealNewlines(s='') {
  // normalize escaped \n into real newlines
  return String(s).replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}
function normalizeLog(s='') { return stripAnsi(toRealNewlines(s)); }
function classifyLine(t) {
  const s = (t || '').trim();
  if (/^\[OK\]/i.test(s))       return 'ok';
  if (/^\[ERROR\]/i.test(s))    return 'error';
  if (/^\[WARNING\]/i.test(s))  return 'warning';
  if (/^\[WARN\]/i.test(s))     return 'warning';
  if (/^\[RETRY\]/i.test(s))    return 'debug';
  if (/^\[DEBUG\]/i.test(s))    return 'debug';
  if (/^\[STATS\]/i.test(s))    return 'stats';
  if (/^\[CANCEL\]/i.test(s))   return 'warning';
  if (/^\[INFO\]/i.test(s))     return 'info';
  return 'info';
}

/* ===============
   Log subcomponent
   =============== */
function JobLogViewer({ job }) {
  const out = normalizeLog(job?.stdout || '');
  const err = normalizeLog(job?.stderr || '');
  const joined = [
    out && '=== STDOUT ===\n' + out,
    err && '=== STDERR ===\n' + err,
  ].filter(Boolean).join('\n');

  const lines = joined.split('\n').map(text => ({ text, level: classifyLine(text) }));

  return (
    <pre style={{
      margin: 0, whiteSpace:'pre-wrap',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: 12, lineHeight: 1.6
    }}>
      {lines.map((ln, i) => {
        let color = '#e6eaff';
        if (ln.level === 'ok')         color = '#a5d6a7';
        else if (ln.level === 'error') color = '#ff6b6b';
        else if (ln.level === 'warning') color = '#ffb74d';
        else if (ln.level === 'debug')   color = '#90caf9';
        else if (ln.level === 'stats')   color = '#a5d6a7';
        return <span key={i} style={{ color }}>{ln.text + '\n'}</span>;
      })}
    </pre>
  );
}

export default class InventoryControlCenter extends React.Component {
  constructor(props) {
    super(props);
    // Use singleton if available, otherwise create a local client immediately.
    const client = (typeof window !== 'undefined' && window.client)
      ? window.client
      : new Client(Config.API_PREFIX || '');
    // Only wrap if those methods exist
    if (client) {
      onceWrap(client, 'setConfigLocalStorage');
      onceWrap(client, 'setLanguagesLocalStorage');
    }
    this.state = {
      client,

      // overview / selection
      loading: true,
      rows: [],
      filterText: '',
      selectedLocId: '',
      selectedLocName: '',
      selectedMonth: '',

      // month_close (no id_field anymore)
      mc_overwrite: false,
      mc_dry_run: false,

      // salesSyncConsumption
      ssc_lookBack: '',
      ssc_force: false,

      // import_opening_close
      ioc_overwrite: false,
      ioc_dry_run: false,
      ioc_file: null,

      // import_inventory_csv
      iinv_file: null,
      iinv_replace: true,
      iinv_stamp: true,
      iinv_central_label: '',

      // import_movements_csv
      imov_org: 'Cartofisserie S.A.',
      imov_schema: 'cartofisserie UN/33 RON',
      imov_from: '',
      imov_to: '',
      imov_no_close_default: false,
      imov_timeout: 120,
      imov_verify: true,
      imov_keep: false,
      imov_locations: '',

      // import_receipts_csv
      irec_from: '',
      irec_to: '',
      irec_no_close_default: false,
      irec_timeout: 90,
      irec_verify: true,
      irec_keep: false,

      // import_internal_use_csv
      iiuse_file: null,
      imr_file: null,

      // apply_close_to_history
      ach_product: '',         // csv irefs, optional
      ach_delivery: '',        // '', '0', '1'
      ach_inline_timeout: '8', // seconds; 0 → force background
      ach_all_unsynced: false, // if true → ignore month/year, process all unsynced

      // UI
      running: false,
      log: [], // [{ts, cmd, req, res, ok, lines:[{text, level}] }]
      snack: { open:false, msg:'', severity:'info' },

      // precheck UX
      precheckByLoc: {},
      precheckDialogOpen: false,
      precheckDialogList: [],

      // Jobs modal
      jobsModalOpen: false,
      jobsLoading: false,
      jobs: [],
      jobSelected: null,
      jobPollTimer: null,
      jobPolling: false,
    };

    this.fileRefIOC = React.createRef();
    this.fileRefIINV = React.createRef();
    this.fileRefIIUSE = React.createRef();
    this.fileRefIMR = React.createRef();
  }

  componentDidMount() {
    if (process.env.NODE_ENV !== 'production') {
      if (__iccDidMountOnce) return;
      __iccDidMountOnce = true;
    }
    setTimeout(() => this.loadOverview(), 0);
  }

  // ---------- Client wrappers ----------
  precheckOpening = (locId, month) => {
    const { client } = this.state;
    const url = client.prefix + API.OPENING_PRECHECK;
    let result = null;
    $.ajax({
      async: false,
      type: 'GET',
      url,
      data: { month, location: locId || 'ALL' },
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      contentType: 'application/json',
      cache: false,
      format: 'json',
      success: function (msg) { result = msg; },
      error: function (xhr) {
        result = xhr.responseJSON || {
          ok: false,
          message: (xhr.responseText || ('HTTP ' + xhr.status + ' ' + (xhr.statusText || 'Error'))),
          status: xhr.status,
        };
      }
    });
    return result;
  };
  getData = (url, query = {}) => {
    const c = this.state.client;
    if (!c || typeof c.getData !== 'function') return {};
    return c.getData(url, query) || {};
  };
  pushData = (url, payload={}) => this.state.client.pushData(url, payload) || {};
  uploadFile = (url, formData) => {
    const { client } = this.state;
    let result = null;
    $.ajax({
      async: false,
      data: formData,
      processData: false,
      contentType: false,
      type: 'POST',
      url: client.prefix + url,
      cache: false,
      success: function (msg) { result = msg; },
      error: function (xhr) { result = xhr.responseJSON || { ok:false, message:'Upload failed' }; }
    });
    return result || { ok:false, message:'No response' };
  };
  pickMsg = (res, fallback) => {
    if (!res) return fallback;
    return res.message || res.detail || (res.status ? `HTTP ${res.status}` : fallback);
  };

  // ---------- Data ----------
  loadOverview = () => {
    try {
      this.setState({ loading:true });
      const raw = this.getData(API.OVERVIEW, {});
      const rows = (Array.isArray(raw) ? raw : []).map(r => {
        const last_close_month = normalizeYYYYMM(r.last_close_month);
        const lines_count = Number(
          r.lines_count ?? r.linesCount ?? (r.stats && r.stats.lines_count) ?? 0
        );
        const computedBehind = computeBehindMonths(last_close_month);
        const behind_months =
          (typeof r.behind_months === 'number' && last_close_month)
            ? r.behind_months
            : computedBehind;

        return {
          ...r,
          last_close_month,
          lines_count,
          behind_months,
        };
      });

      /* const keyMonth = (s) => (s || '0000-00');
      rows.sort((a, b) => {
        const am = keyMonth(a.last_close_month), bm = keyMonth(b.last_close_month);
        if (am < bm) return -1; if (am > bm) return 1;
        const an = (a.location_name || '').toLowerCase();
        const bn = (b.location_name || '').toLowerCase();
        if (an < bn) return -1; if (an > bn) return 1;
        return 0;
      }); */

      this.setState({ rows, loading:false });
    } catch (err) {
      this.pushLog('overview', {}, err, false);
      this.toast('Failed to load overview', 'error');
      this.setState({ loading:false });
    }
  };

  // ---------- Selection helpers ----------
  get locationOptions() {
    const base = (this.state.rows || []).map(r => ({ id: r.location_id, label: r.location_name || String(r.location_id) }));
    return [{ id: 'ALL', label: 'All locations' }, ...base];
  }

  precheckAllToState = async (month) => {
    if (!month) return;
    try {
      const resp = await this.precheckOpening('ALL', month);
      if (resp?.ok) {
        const map = {};
        (resp.results || []).forEach(r => { map[r.location_id] = r.count || 0; });
        this.setState({ precheckByLoc: map });
      }
    } catch (e) { /* ignore */ }
  };

  autoPrecheck = async (locId, month) => {
    if (!locId || !month) return;
    try {
      const resp = await this.precheckOpening(locId, month);
      if (resp?.ok && Array.isArray(resp.results) && resp.results.length) {
        const count = resp.results[0]?.count ?? 0;
        this.setState(s => ({
          precheckByLoc: { ...s.precheckByLoc, [locId]: count }
        }));
        if (count > 1) {
          this.toast(`Duplicates found for ${month} at this location (${count}).`, 'error');
        }
      }
    } catch (e) { /* silent */ }
  };

  setFromRow = (row) => {
    this.setState({
      selectedLocId: row.location_id,
      selectedLocName: row.location_name,
      selectedMonth: row.last_close_month || '',
      imov_from: firstDayOfMonth(row.last_close_month || ''),
      imov_to: lastDayOfMonth(row.last_close_month || ''),
      irec_from: firstDayOfMonth(row.last_close_month || ''),
      irec_to: lastDayOfMonth(row.last_close_month || ''),
    }, () => {
      this.autoPrecheck(this.state.selectedLocId, this.state.selectedMonth);
    });
  };

  downloadOpeningTemplateLocal = () => {
    const { selectedLocName } = this.state;
    const orgHint = selectedLocName || 'ORG_EXT_RELATION';
    const rows = [
      ['Organization','Product','Quantity','Price'],
      [orgHint, 'PROD_RELATION_1001', '10', '12.50'],
      [orgHint, 'PROD_RELATION_2002', '3', '5.00'],
    ];
    const blob = buildCsv(rows);
    downloadBlob(blob, 'opening_snapshot_template.csv');
    this.toast('Template generated', 'success');
  };

  downloadInventoryTemplateLocal = () => {
    const rows = [
      ['Location','Product','Movement type','Doc No','Date','Stoc IN','Stoc OUT','Price'],
      ['ORG_OR_STORE_LABEL','PROD_RELATION_1001','Transfer OUT','DOC-123','2025-08-01','0','5','12.50'],
      ['ORG_OR_STORE_LABEL','PROD_RELATION_1001','Transfer IN','DOC-123','2025-08-01','5','0','12.50'],
    ];
    const blob = buildCsv(rows);
    downloadBlob(blob, 'inventory_import_template.csv');
    this.toast('Template generated', 'success');
  };

  // ---------- Log handling with levels ----------
  parseOutputToLines = (res) => {
    const chunks = [];
    if (res?.stats?.stdout) chunks.push(String(res.stats.stdout));
    if (res?.stdout)        chunks.push(String(res.stdout));
    if (typeof res?.message === 'string') chunks.push(res.message);
    if (Array.isArray(res?.warnings) && res.warnings.length) {
      chunks.push(['Warnings:', ...res.warnings.map(w => ` - ${w}`)].join('\n'));
    }
    if (Array.isArray(res?.errors) && res.errors.length) {
      chunks.push(['Errors:', ...res.errors.map(e => ` - ${e}`)].join('\n'));
    }
    if (!chunks.length) chunks.push(safeStringify(res));

    const raw = chunks.join('\n');
    const lines = normalizeLog(raw).split(/\r?\n/);

    const classify = (t) => {
      const s = (t || '').trim();
      if (/^\[ERROR\]/i.test(s) || /\b(error|failed|traceback)\b/i.test(s)) return 'error';
      if (/^\[WARNING\]/i.test(s) || /\bwarn(ing)?\b/i.test(s)) return 'warning';
      if (/^\[DEBUG\]/i.test(s)) return 'debug';
      if (/^\[STATS\]/i.test(s)) return 'stats';
      if (/^\[INFO\]/i.test(s)) return 'info';
      if (/^❌/.test(s)) return 'error';
      if (/^⚠️/.test(s)) return 'warning';
      return 'info';
    };

    return lines.map(text => ({ text, level: classify(text) }));
  };

  pushLog = (cmd, req, res, ok=true) => {
    const entry = {
      ts: new Date().toISOString(),
      cmd,
      req,
      res,
      ok,
      lines: this.parseOutputToLines(res),
    };
    this.setState(s => ({ log: [entry, ...s.log].slice(0, 500) }));
  };

  toast = (msg, severity='info') => this.setState({ snack:{ open:true, msg, severity }});
  closeSnack = () => this.setState({ snack: { ...this.state.snack, open:false } });

  // ---------- Commands ----------
  runMonthClose = () => {
    const { selectedLocId, selectedLocName, selectedMonth, mc_overwrite, mc_dry_run } = this.state;
    if (!selectedMonth) return this.toast('Pick month', 'warning');
    const location = selectedLocId || 'ALL';
    const payload = { location, month: selectedMonth, overwrite: mc_overwrite, dry_run: mc_dry_run };
    this.setState({ running:true }, () => {
      const res = this.pushData(API.MONTH_CLOSE, payload);
      const ok = res?.ok !== false;
      const label = (location === 'ALL') ? 'ALL locations' : (selectedLocName || location);
      this.pushLog(`month_close (${label})`, payload, res, ok);
      this.toast(ok ? 'month_close ok' : 'month_close failed', ok ? 'success' : 'error');
      this.setState({ running:false }, () => { if (ok) this.loadOverview(); });
    });
  };

  runApplyClose = () => {
    const {
      selectedLocId, selectedLocName, selectedMonth,
      ach_product, ach_delivery, ach_inline_timeout, ach_all_unsynced
    } = this.state;

    // Build params
    const params = {};
    // Location: send only if not ALL/empty
    if (selectedLocId && selectedLocId !== 'ALL') params.location = selectedLocId;

    // Year/Month: include only if not "all unsynced" and month is chosen
    if (!ach_all_unsynced && selectedMonth) {
      try {
        const [y, m] = selectedMonth.split('-').map(Number);
        if (y && m) { params.year = y; params.month = m; }
      } catch {}
    }

    // Optional product and delivery
    if (ach_product && ach_product.trim()) params.product = ach_product.trim();
    if (ach_delivery === '0' || ach_delivery === '1') params.delivery = ach_delivery;

    const inlineTimeout = Number(ach_inline_timeout) || 0;
    const url = API.APPLY_CLOSE + `?inline_timeout=${inlineTimeout}`;

    this.setState({ running:true }, () => {
      const res = this.pushData(url, params);
      const ok = res?.ok !== false;
      const scope =
        ach_all_unsynced || !selectedMonth
          ? 'ALL UNSYNCED'
          : (selectedMonth + (selectedLocName ? ` @ ${selectedLocName}` : (selectedLocId || '')));
      this.pushLog('apply_close_to_history', { scope, params }, res, ok);

      // Background?
      if (ok && res?.mode === 'background' && res?.job_id) {
        this.toast('Apply Close started in background', 'info');
        this.openJobs(res.job_id);
      } else {
        this.toast(ok ? 'Apply Close completed' : 'Apply Close failed', ok ? 'success' : 'error');
      }
      this.setState({ running:false });
    });
  };

  runSalesSync = () => {
    const { ssc_lookBack, ssc_force } = this.state;
    const payload = {};
    if (ssc_lookBack) payload.lookBack = Number(ssc_lookBack);
    if (ssc_force) payload.force = true;
    this.setState({ running:true }, () => {
      const res = this.pushData(API.SALES_SYNC, payload);
      const ok = res?.ok !== false;
      this.pushLog('salesSyncConsumption', payload, res, ok);
      this.toast(ok ? 'salesSyncConsumption ok' : 'salesSyncConsumption failed', ok ? 'success' : 'error');
      this.setState({ running:false });
    });
  };

  openPrecheckModalForAll = async () => {
    const { selectedMonth } = this.state;
    if (!selectedMonth) return this.toast('Pick month', 'warning');
    const resp = await this.precheckOpening('ALL', selectedMonth);
    this.pushLog('opening_close_precheck', { month: selectedMonth, location: 'ALL' }, resp, !!resp?.ok);
    if (!resp?.ok) return this.toast(this.pickMsg(resp, 'Precheck failed'), 'error');
    const list = Array.isArray(resp.results) ? resp.results : [];
    this.setState({ precheckDialogOpen: true, precheckDialogList: list });
  };

  pickIOC = () => {
    const { selectedLocId, selectedMonth } = this.state;
    if (!selectedMonth) return this.toast('Select month', 'warning');
    if (!selectedLocId) return this.toast('Select location (or All for Check)', 'warning');
    if (selectedLocId === 'ALL') {
      return this.openPrecheckModalForAll();
    }
    this.fileRefIOC.current?.click();
  };
  onPickedIOC = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    this.setState({ ioc_file:file }, this.uploadIOC);
  };
  uploadIOC = async () => {
    const { selectedLocId, selectedLocName, selectedMonth, ioc_overwrite, ioc_dry_run, ioc_file } = this.state;
    if (!ioc_file) return this.toast('Pick a file', 'warning');
    const inlineTimeout = inlineTimeoutForFile(ioc_file);

    const pre = await this.precheckOpening(selectedLocId, selectedMonth);
    if (!pre?.ok) {
      this.pushLog('opening_close_precheck', { month: selectedMonth, location: selectedLocId }, pre, false);
      return this.toast(this.pickMsg(pre, 'Precheck failed'), 'error');
    }
    const rec = (pre.results || [])[0];
    if (rec?.count > 1) {
      this.pushLog('opening_close_precheck', { month: selectedMonth, location: selectedLocId }, pre, false);
      return this.toast(`Duplicate month close found (${rec.count}). Resolve before importing.`, 'error');
    }
    if (rec?.count === 1 && !ioc_overwrite && !ioc_dry_run) {
      const ok = window.confirm(`A month close already exists for ${selectedLocName} ${selectedMonth}. Overwrite lines?`);
      if (!ok) return;
    }

    const form = new FormData();
    form.append('file', ioc_file);
    form.append('location', selectedLocId);
    form.append('month', selectedMonth);
    if (ioc_overwrite) form.append('overwrite','true');
    if (ioc_dry_run) form.append('dry_run','true');

    this.setState({ running:true }, async () => {
      const url = API.IMPORT_OPENING_CLOSE + `?inline_timeout=${inlineTimeout}`;
      const res = await this.uploadFile(url, form);
      const ok = res?.ok !== false;
      this.pushLog(`import_opening_close (${selectedLocName})`, { multipart:true }, res, ok);
      if (ok && res?.mode === 'background' && res?.job_id) {
        this.toast('Opening snapshot started in background', 'info');
        this.openJobs(res.job_id);
      } else {
        this.toast(ok ? 'Opening snapshot completed' : 'Opening snapshot failed', ok ? 'success' : 'error');
      }
      this.setState({ running:false }, () => { if (ok) this.loadOverview(); });
    });
  };

  pickIINV = () => this.fileRefIINV.current?.click();
  onPickedIINV = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    this.setState({ iinv_file:file }, this.uploadIINV);
  };
  uploadIINV = () => {
    const { iinv_file, iinv_replace, iinv_stamp, iinv_central_label } = this.state;
    if (!iinv_file) return this.toast('Pick a file', 'warning');
    if (!iinv_file || iinv_file.size === 0) {
      return this.toast('Selected file is empty.', 'warning');
    }
    const inlineTimeout = inlineTimeoutForFile(iinv_file);
    const form = new FormData();
    form.append('file', iinv_file);
    form.append('replace', String(!!iinv_replace));
    form.append('stamp_central', String(!!iinv_stamp));
    if (iinv_central_label) form.append('central_label', iinv_central_label);

    this.setState({ running:true }, () => {
      const url = API.IMPORT_INVENTORY_CSV + `?inline_timeout=${inlineTimeout}`;
      const res = this.uploadFile(url, form);
      const ok = res?.ok !== false;
      this.pushLog('import_inventory_csv', { multipart:true }, res, ok);
      if (ok && res?.mode === 'background' && res?.job_id) {
        this.toast('Inventory import started in background', 'info');
        this.openJobs(res.job_id);
      } else {
        this.toast(ok ? 'Inventory import completed' : 'Inventory import failed', ok ? 'success' : 'error');
      }
      this.setState({ running:false });
    });
  };

  pickIMR = () => this.fileRefIMR?.current && this.fileRefIMR.current.click();
  onPickedIMR = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    this.setState({ imr_file: file }, this.uploadIMR);
  };
  uploadIMR = () => {
    const { imr_file } = this.state;
    if (!imr_file) return this.toast('Pick a file', 'warning');
    if (imr_file.size === 0) return this.toast('Selected file is empty.', 'warning');

    const form = new FormData();
    form.append('file', imr_file);

    this.setState({ running: true }, () => {
      const url = API.IMPORT_INTERNAL_MR_CSV;
      const res = this.uploadFile(url, form);
      const ok = res?.ok !== false;

      // same improved toasts you already have for IIUSE:
      this.pushLog('import_internal_material_receipt_csv', { multipart: true }, res, ok);

      const hasCounts = Number.isFinite(res?.sent) && Number.isFinite(res?.total);
      const counts = hasCounts ? `${res.sent}/${res.total} sent, ${res.failed ?? 0} failed` : null;
      const msg = res?.summary || counts || (ok ? 'Material Receipt import completed' : 'Material Receipt import failed');
      const color = res?.status === 200 ? 'success' : res?.status === 207 ? 'warning' : 'error';
      this.toast(msg, color);

      if (Array.isArray(res?.failed_keys) && res.failed_keys.length) {
        const head = res.failed_keys.slice(0, 3).map(k => `${k.DocumentNo}/${k.Organization}`).join(', ');
        const more = res.failed_keys.length > 3 ? ` +${res.failed_keys.length - 3} more` : '';
        this.toast(`Failed docs: ${head}${more}`, 'warning');
        this.pushLog('import_internal_material_receipt_csv', { failed_keys: res.failed_keys }, null, false);
      }
      if (res?.last_response_preview) {
        this.pushLog('import_internal_material_receipt_csv', { last_response_preview: res.last_response_preview }, null, true);
      }

      if (ok && res?.mode === 'background' && res?.job_id) {
        this.toast('Material Receipt import started in background', 'info');
        this.openJobs(res.job_id);
      }

      this.setState({ running: false });
    });
  };

  downloadInternalUseTemplateLocal = () => {
    const { selectedLocName } = this.state;
    const orgHint = selectedLocName || 'ORG_OR_STORE_LABEL';
    const rows = [
      ['location','organization','product','qty','documentno','description','movement_date','warehouse','documentaction'],
      [orgHint, orgHint, 'PROD_RELATION_1001', '2', 'BCimport0969', 'Consum iul25', '2025-07-31', orgHint, 'Complete'],
      [orgHint, orgHint, 'Aripioare pui Crispy 1kg', '36.75', 'BCimport0969', 'Consum iul25', '2025-07-31', orgHint, 'Complete'],
      [orgHint, orgHint, 'arpagic', '5', 'BCimport0969', 'Consum iul25', '2025-07-31', orgHint, 'Complete'],
    ];
    const blob = buildCsv(rows);
    downloadBlob(blob, 'internal_use_import_template.csv');
    this.toast('Template generated', 'success');
  };

  pickIIUSE = () => this.fileRefIIUSE?.current && this.fileRefIIUSE.current.click();

  onPickedIIUSE = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    this.setState({ iiuse_file: file }, this.uploadIIUSE);
  };

  uploadIIUSE = () => {
    const { iiuse_file } = this.state;

    if (!iiuse_file) {
      this.toast('Pick a file', 'warning');
      return;
    }
    if (iiuse_file.size === 0) {
      this.toast('Selected file is empty.', 'warning');
      return;
    }

    const form = new FormData();
    form.append('file', iiuse_file);

    this.setState({ running: true }, () => {
      const url = API.IMPORT_INTERNAL_USE_CSV; // backend controls cfg + inline/background
      const res = this.uploadFile(url, form);
      const ok = res?.ok !== false;

      // Log the server response (so you can inspect counts/preview in Execution Log)
      this.pushLog('import_internal_use_csv', { multipart: true }, res, ok);

      // Build a friendly message
      const hasCounts = Number.isFinite(res?.sent) && Number.isFinite(res?.total);
      const counts = hasCounts ? `${res.sent}/${res.total} sent, ${res.failed ?? 0} failed` : null;
      const msg = res?.summary || counts || (ok ? 'Internal Use import completed' : 'Internal Use import failed');

      // Color by outcome: 200=success, 207=partial, else=error
      const color =
        res?.status === 200 ? 'success' :
        res?.status === 207 ? 'warning' :
        'error';
      this.toast(msg, color);

      // If backend provided which docs failed, surface a short preview + log the full list
      if (Array.isArray(res?.failed_keys) && res.failed_keys.length) {
        const head = res.failed_keys
          .slice(0, 3)
          .map(k => `${k.DocumentNo}/${k.Organization}`)
          .join(', ');
        const more = res.failed_keys.length > 3 ? ` +${res.failed_keys.length - 3} more` : '';
        this.toast(`Failed docs: ${head}${more}`, 'warning');
        this.pushLog('import_internal_use_csv', { failed_keys: res.failed_keys }, null, false);
      }

      // Optional: show the last ERP response preview line, if returned
      if (res?.last_response_preview) {
        this.pushLog('import_internal_use_csv', { last_response_preview: res.last_response_preview }, null, true);
      }

      // Background promotion path
      if (ok && res?.mode === 'background' && res?.job_id) {
        this.toast('Internal Use import started in background', 'info');
        this.openJobs(res.job_id);
      }

      this.setState({ running: false });
    });
  };
  downloadMaterialReceiptTemplateLocal = () => {
    const { selectedLocName } = this.state;
    const org = selectedLocName || 'ORG_OR_STORE_LABEL';
    const rows = [
      ['location','organization','product','qty','price','documentno','description','movement_date','pricelist','warehouse','documentaction'],
      [org, org, 'prod1', '2', '6.37', 'MRimport0001', 'Receipt Aug25', '2025-08-31', 'Achizitie Generala RON', org, 'Complete'],
      [org, org, 'prod2', '33', '1.76', 'MRimport0001', 'Receipt Aug25', '2025-08-31', 'Achizitie Generala RON', org, 'Complete'],
    ];
    const blob = buildCsv(rows);
    downloadBlob(blob, 'material_receipt_import_template.csv');
    this.toast('Template generated', 'success');
  };

  runMovements = () => {
    const {
      imov_org, imov_schema, imov_from, imov_to, imov_no_close_default,
      imov_timeout, imov_verify, imov_keep, imov_locations
    } = this.state;
    const payload = {
      org_name: imov_org,
      acct_schema: imov_schema,
      timeout: Number(imov_timeout) || 120,
      verify: !!imov_verify,
      keep: !!imov_keep,
    };
    if (imov_from) payload.date_from = imov_from;
    if (imov_to) payload.date_to = imov_to;
    if (imov_no_close_default) payload.no_close_default = true;
    if (imov_locations) payload.locations = imov_locations.split(',').map(s => s.trim()).filter(Boolean);

    this.setState({ running:true }, () => {
      const res = this.pushData(API.IMPORT_MOVEMENTS_CSV, payload);
      const ok = res?.ok !== false;
      this.pushLog('import_movements_csv', payload, res, ok);
      this.toast(ok ? 'import_movements_csv ok' : 'import_movements_csv failed', ok ? 'success' : 'error');
      this.setState({ running:false });
    });
  };

  runReceipts = () => {
    const { irec_from, irec_to, irec_no_close_default, irec_timeout, irec_verify, irec_keep } = this.state;
    const payload = {
      timeout: Number(irec_timeout) || 90,
      verify: !!irec_verify,
      keep: !!irec_keep,
    };
    if (irec_from) payload.date_from = irec_from;
    if (irec_to) payload.date_to = irec_to;
    if (irec_no_close_default) payload.no_close_default = true;

    this.setState({ running:true }, () => {
      const res = this.pushData(API.IMPORT_RECEIPTS_CSV, payload);
      const ok = res?.ok !== false;
      this.pushLog('import_receipts_csv', payload, res, ok);
      this.toast(ok ? 'import_receipts_csv ok' : 'import_receipts_csv failed', ok ? 'success' : 'error');
      this.setState({ running:false });
    });
  };

  // ---------- Jobs modal wiring ----------
  openJobs = () => {
    this.setState(
      {
        jobsModalOpen: true,
        jobsLoading: true,
        jobs: [],
        jobSelected: null,
        jobPolling: false,
        jobPollTimer: null,
      },
      this.refreshJobsList // will also pick latest job
    );
  };

  // Close modal + stop polling
  closeJobs = () => {
    const { jobPollTimer } = this.state || {};
    if (jobPollTimer) clearInterval(jobPollTimer);
    this.setState({
      jobsModalOpen: false,
      jobsLoading: false,
      jobs: [],
      jobSelected: null,
      jobPolling: false,
      jobPollTimer: null,
    });
  };

  // Fetch recent jobs; select latest (index 0). Optionally begin polling that job.
  refreshJobsList = () => {
    try {
      // Expecting { ok: true, results: [...] } where results is newest-first (your API already orders by started_at desc)
      const listResp = this.getData(API.JOBS); // e.g., /jobs/
      const jobs = (listResp?.results || []).slice(); // copy array

      this.setState({ jobsLoading: false, jobs }, () => {
        // Autoselect latest job if none selected
        if (!this.state.jobSelected && jobs.length) {
          const latest = jobs[0];
          this.loadJobDetail(latest.id, { startPolling: true });
        }
      });
    } catch (e) {
      this.setState({ jobsLoading: false });
      this.toast('Failed to load jobs', 'error');
    }
  };

  // Load detail for a job and (optionally) start polling if RUNNING/PENDING
  loadJobDetail = (jobId, { startPolling = false } = {}) => {
    try {
      // If your backend is path-based: const detail = this.getData(API.JOB_DETAIL + encodeURIComponent(jobId) + '/');
      const detail = this.getData(API.JOB_DETAIL, { id: jobId });
      if (detail?.ok) {
        const active = ['RUNNING', 'PENDING'].includes(detail.status);

        this.setState({ jobSelected: detail, jobsLoading: false }, () => {
          // auto-scroll log to end
          if (this.jobLogRef?.current) {
            const el = this.jobLogRef.current;
            el.scrollTop = el.scrollHeight;
          }
        });

        if (startPolling && active) this._startJobPolling(jobId);
        if (!active) this._stopJobPolling();
      } else {
        this.toast(this.pickMsg(detail, 'Failed to load job detail'), 'error');
        this._stopJobPolling();
      }
    } catch (e) {
      this.toast('Failed to load job detail', 'error');
      this._stopJobPolling();
    }
  };

  // Start/stop polling selected job (every 1.5s)
  _startJobPolling = (jobId) => {
    if (this.state.jobPollTimer) return; // avoid duplicates
    const id = setInterval(() => {
      if (!this.state.jobsModalOpen) return this._stopJobPolling(); // safety
      // If you navigate to another job, polling reuses same timer since we call loadJobDetail with new id
      this.loadJobDetail(jobId, { startPolling: false });
    }, 1500);
    this.setState({ jobPolling: true, jobPollTimer: id });
  };

  _stopJobPolling = () => {
    const { jobPollTimer } = this.state || {};
    if (jobPollTimer) clearInterval(jobPollTimer);
    this.setState({ jobPolling: false, jobPollTimer: null });
  };

  // Safety: clear polling when component unmounts
  componentWillUnmount() {
    const { jobPollTimer } = this.state || {};
    if (jobPollTimer) clearInterval(jobPollTimer);
  };

  // refreshJobsList = () => {
  //   const { client } = this.state;
  //   $.ajax({
  //     type: 'GET',
  //     url: (client?.prefix || '') + API.JOBS,
  //     headers: { 'Accept': 'application/json' },
  //     success: (msg) => {
  //       const arr = (msg?.results || []).map(r => ({
  //         id: r.id, kind: r.kind, status: r.status, started_at: r.started_at, finished_at: r.finished_at, params: r.params
  //       }));
  //       this.setState({ jobs: arr, jobsLoading: false }, () => {
  //         const cur = this.state.jobSelected?.id;
  //         const first = arr[0]?.id;
  //         if (!cur && first) this.selectJob(first);
  //       });
  //     },
  //     error: () => {
  //       this.setState({ jobsLoading: false });
  //       this.toast('Failed to load jobs', 'error');
  //     }
  //   });
  // };

  selectJob = (id) => {
    this.fetchJobDetail(id, true);
  };

  fetchJobDetail = (id, startPolling=false) => {
    const { client } = this.state;
    // PATH param: /job_detail/<id>/
    const url = (client?.prefix || '') + API.JOB_DETAIL + encodeURIComponent(id) + '/';
    $.ajax({
      type: 'GET',
      url,
      headers: { 'Accept': 'application/json' },
      success: (msg) => {
        if (!msg?.ok) return;
        const job = {
          id: msg.id, kind: msg.kind, status: msg.status,
          started_at: msg.started_at, finished_at: msg.finished_at,
          params: msg.params, stdout: msg.stdout || '', stderr: msg.stderr || '',
          cancel_requested: !!msg.cancel_requested
        };
        const live = ['RUNNING','PENDING'].includes(job.status);
        if (startPolling && live && !this.state.jobPollTimer) {
          const t = setInterval(() => this.fetchJobDetail(id, false), 1500);
          this.setState({ jobPollTimer: t, jobPolling: true });
        }
        if (!live && this.state.jobPollTimer) {
          clearInterval(this.state.jobPollTimer);
          this.setState({ jobPollTimer: null, jobPolling: false });
        }
        this.setState({ jobSelected: job });
      },
      error: () => this.toast('Failed to load job detail', 'error')
    });
  };

  cancelJob = () => {
    const job = this.state.jobSelected;
    if (!job) return;
    const { client } = this.state;
    // PATH param: /job_cancel/<id>/
    const url = (client?.prefix || '') + API.JOB_CANCEL + encodeURIComponent(job.id) + '/';
    $.ajax({
      type: 'POST',
      url,
      data: JSON.stringify({}), // body not required; using path param
      contentType: 'application/json',
      success: (msg) => {
        this.toast(msg?.message || 'Cancel requested', 'info');
        setTimeout(() => this.fetchJobDetail(job.id, false), 600);
      },
      error: (xhr) => {
        const e = xhr?.responseJSON || {};
        this.toast(e.message || 'Cancel failed', 'error');
      }
    });
  };

  downloadJobLogs = () => {
    const job = this.state.jobSelected;
    const out = normalizeLog(job?.stdout || '');
    const err = normalizeLog(job?.stderr || '');
    const joined = [
      out && '=== STDOUT ===\n' + out,
      err && '=== STDERR ===\n' + err,
    ].filter(Boolean).join('\n');
    const blob = new Blob([joined], { type: 'text/plain;charset=utf-8;' });
    downloadBlob(blob, `job_${job?.id || 'logs'}.log`);
  };

  jobBanner = (job) => {
    if (!job) return { severity: 'info', title: 'Loading…', text: '' };
    const s = job.status;
    const hasErrorLines = /\[ERROR\]/i.test(job.stderr || '');
    const hasWarnings  = /\[WARN(ING)?\]/i.test((job.stdout || '') + (job.stderr || ''));
    const live = ['RUNNING','PENDING'].includes(s);

    if (live) return { severity: 'info', title: s === 'RUNNING' ? 'Running…' : 'Pending…', text: 'Logs are streaming.' };
    if (s === 'FAILED') return { severity: 'error', title: 'Job failed', text: 'A fatal error stopped this job.' };
    if (s === 'CANCELLED') return { severity: 'warning', title: 'Cancelled', text: 'Job was cancelled.' };
    if (hasErrorLines) return { severity: 'warning', title: 'Success (validation issues)', text: 'Completed with non-fatal issues.' };
    if (hasWarnings) return { severity: 'info', title: 'Success (with warnings)', text: 'Completed with warnings.' };
    return { severity: 'success', title: 'Success', text: 'Completed without issues.' };
  };

  // ---------- UI bits ----------
  behindChip = (n) => {
    if (n === null || n === undefined) return null;
    const color = n === 0 ? 'success' : (n <= 2 ? 'warning' : 'error');
    return <Chip size="small" color={color} label={`${n}`} />;
  };

  ActionRow = ({ label, children, runButton }) => (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '120px minmax(0, 1fr)',
            md: '20px 140px minmax(0, 1fr) auto',
          },
          alignItems: {
            xs: 'stretch',
            md: 'center',
          },
          gap: {
            xs: 0.5,
            sm: 0.75,
            md: 1,
          },
          py: 0.5,
          '& .MuiButton-root': compactButtonSx,
          '& .MuiTextField-root': compactFieldSx,
          '& .MuiAutocomplete-root': {
            minWidth: 0,
          },
          '& .MuiFormControlLabel-root': {
            mr: { xs: 0.5, sm: 1 },
          },
          '& .MuiFormControlLabel-label': {
            fontSize: {
              xs: '0.76rem',
              sm: '0.84rem',
            },
          },
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            opacity: 0.6,
            display: {
              xs: 'none',
              md: 'block',
            },
          }}
        >
          →
        </Typography>
        <Typography
          sx={{
            fontWeight: 750,
            textAlign: 'left',
            opacity: 0.85,
            fontSize: {
              xs: '0.8rem',
              sm: 13,
            },
          }}
        >
          {label}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          sx={{
            minWidth: 0,
            '& .MuiButton-root': compactButtonSx,
          }}
        >
          {children}
        </Stack>
        <Box
          sx={{
            display: 'flex',
            justifyContent: {
              xs: 'flex-start',
              md: 'flex-end',
            },
            mt: {
              xs: 0.25,
              md: 0,
            },
          }}
        >
          {runButton}
        </Box>
      </Box>
      <Divider sx={{ my: 0.5 }} />
    </>
  );

  render() {
    const s = this.state;
    const filtered = s.rows.filter(r =>
      !s.filterText ||
      (r.location_name?.toLowerCase().includes(s.filterText.toLowerCase())) ||
      (r.last_close_month?.includes(s.filterText))
    );

    return (
      <Box sx={pageRootSx}>
        <Grid container spacing={1} sx={mainGridSx}>
          {/* LEFT: compact table */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              height: {
                xs: '45%',
                md: 'calc(100% - 138px)',
              },
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Paper sx={scrollPaperSx}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  mb: 0.75,
                  justifyContent: 'space-between',
                  '& .MuiButton-root': compactButtonSx,
                  '& .MuiTextField-root': compactFieldSx,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex:1 }}>
                  <TextField
                    size="small"
                    placeholder="Filter name / YYYY-MM"
                    value={s.filterText}
                    onChange={(e)=>this.setState({ filterText:e.target.value })}
                    onKeyDown={(e)=>{ if (e.key === 'Escape') this.setState({ filterText:'' }); }}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        s.filterText ? (
                          <Button
                            size="small"
                            onClick={()=>this.setState({ filterText:'' })}
                            startIcon={<ClearIcon fontSize="small" />}
                            sx={{ minWidth: 0, px: 1 }}
                            title="Clear"
                          >
                            Clear
                          </Button>
                        ) : null
                      )
                    }}
                  />
                  <Button size="small" onClick={this.loadOverview}>Refresh</Button>
                </Stack>
                <Button size="small" variant="contained" color="secondary" startIcon={<WorkOutlineIcon />} onClick={this.openJobs}>Jobs</Button>
              </Stack>

              <Box sx={tableScrollSx}>
                <Table size="small" stickyHeader sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Last</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Behind</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Lines</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {s.loading ? (
                      <TableRow><TableCell colSpan={4}>Loading…</TableCell></TableRow>
                    ) : filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={4}>No data</TableCell></TableRow>
                    ) : filtered.map((r) => {
                      const selected = r.location_id === s.selectedLocId;
                      const dupCount = this.state.precheckByLoc[r.location_id] || 0;
                      return (
                        <TableRow key={r.location_id}
                                  hover
                                  selected={selected}
                                  onClick={()=>this.setFromRow(r)}
                                  sx={{ cursor:'pointer' }}>
                          <TableCell>{r.location_name}</TableCell>
                          <TableCell>
                            <span>{r.last_close_month || '—'}</span>
                            {dupCount > 1 && (
                              <Chip size="small" sx={{ ml: 0.75 }} color="warning" label={`dup×${dupCount}`} />
                            )}
                            {dupCount === 1 && (
                              <Chip size="small" sx={{ ml: 0.75 }} label="exists" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {r.last_close_month
                              ? this.behindChip(r.behind_months ?? computeBehindMonths(r.last_close_month))
                              : <span style={{ opacity: 0.7 }}>—</span>}
                          </TableCell>
                          <TableCell align="right">{String(r.lines_count ?? '—')}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  '& .MuiTextField-root': compactFieldSx,
                }}
              >
                <TextField
                  size="small"
                  label="Selected"
                  value={s.selectedLocName || s.selectedLocId || ''}
                  InputProps={{ readOnly: true }}
                  sx={{ width: 70 + '%' }}
                />
                <TextField
                  type="month" size="small" label="Month"
                  value={s.selectedMonth}
                  onChange={(e)=>{
                    const v = e.target.value;
                    this.setState({ selectedMonth: v }, ()=>{
                      this.precheckAllToState(v);
                      if (this.state.selectedLocId) this.autoPrecheck(this.state.selectedLocId, v);
                    });
                  }}
                  sx={{ flexGrow: 1, minWidth: 120 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* RIGHT: compact commands with aligned rows and separators */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              height: {
                xs: '55%',
                md: 'calc(100% - 138px)',
              },
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Paper sx={commandPanelSx}>
              <Divider sx={{ mb: 1 }} />

              <this.ActionRow
                label="Opening Snapshot"
                runButton={
                  <Button size="small" variant="contained" startIcon={<UploadFileIcon/>}
                          disabled={s.running} onClick={this.pickIOC}>
                    Upload & Run
                  </Button>
                }
              >
                <Autocomplete
                  options={this.locationOptions}
                  getOptionLabel={(o)=>o.label}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id} 
                  value={this.locationOptions.find(o=>o.id===s.selectedLocId) || null}
                  onChange={(_, val)=>this.setState({
                    selectedLocId: val?.id || '',
                    selectedLocName: val?.label || '',
                  })}
                  renderInput={(params)=>(
                    <TextField
                      {...params}
                      label="Location / All"
                      size="small"
                      sx={{
                        ...compactFieldSx,
                        minWidth: {
                          xs: '100%',
                          sm: 220,
                        },
                      }}
                    />
                  )}
                />
                <TextField
                  type="month" size="small" label="Period"
                  value={s.selectedMonth}
                  onChange={(e)=>{
                    const v = e.target.value;
                    this.setState({ selectedMonth: v }, ()=>{
                      this.precheckAllToState(v);
                      if (this.state.selectedLocId) this.autoPrecheck(this.state.selectedLocId, v);
                    });
                  }}
                  sx={{
                    ...compactFieldSx,
                    width: {
                      xs: 'calc(50% - 6px)',
                      sm: 150,
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button size="small" onClick={async ()=> {
                  if (!s.selectedMonth) return this.toast('Pick month', 'warning');
                  const resp = await this.precheckOpening(s.selectedLocId || 'ALL', s.selectedMonth);
                  this.pushLog('opening_close_precheck', { month: s.selectedMonth, location: s.selectedLocId || 'ALL' }, resp, !!resp?.ok);
                  if (resp?.ok) {
                    const dupes = (resp.results || []).filter(r => r.count > 1);
                    const singles = (resp.results || []).filter(r => r.count === 1);
                    if (dupes.length) this.toast(`Duplicates found for ${resp.month}: ${dupes.length} location(s).`, 'error');
                    else if (singles.length) this.toast(`Existing month close: ${singles.length} location(s).`, 'warning');
                    else this.toast('No existing month close found.', 'info');
                  } else {
                    this.toast(this.pickMsg(resp, 'Precheck failed'), 'error');
                  }
                }}>Check</Button>

                <FormControlLabel
                  control={<Checkbox checked={s.ioc_overwrite} onChange={(e)=>this.setState({ ioc_overwrite:e.target.checked })} />}
                  label="overwrite" />
                <FormControlLabel
                  control={<Checkbox checked={s.ioc_dry_run} onChange={(e)=>this.setState({ ioc_dry_run:e.target.checked })} />}
                  label="dry_run" />
                <Button size="small" variant="outlined" color="success" onClick={this.downloadOpeningTemplateLocal}>CSV template</Button>
                <input type="file" ref={this.fileRefIOC} style={{ display:'none' }}
                      accept=".csv, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={this.onPickedIOC} />
              </this.ActionRow>

              <this.ActionRow
                label="Sales Sync"
                runButton={
                  <Button size="small" variant="contained" startIcon={<PlayArrowIcon/>}
                          disabled={s.running} onClick={this.runSalesSync}>
                    Run
                  </Button>
                }
              >
                <TextField size="small" label="lookBack" value={s.ssc_lookBack}
                           onChange={(e)=>this.setState({ ssc_lookBack:e.target.value })}
                           sx={{ width: 120 }} />
                <FormControlLabel
                  control={<Checkbox checked={s.ssc_force} onChange={(e)=>this.setState({ ssc_force:e.target.checked })} />}
                  label="force" />
              </this.ActionRow>

              <this.ActionRow
                label="Receipts"
                runButton={
                  <Button size="small" variant="contained" startIcon={<PlayArrowIcon/>}
                          disabled={s.running} onClick={this.runReceipts}>
                    Run
                  </Button>
                }
              >
                <FormControlLabel
                  control={<Checkbox checked={s.irec_no_close_default} onChange={(e)=>this.setState({ irec_no_close_default:e.target.checked })} />}
                  label="no_close_default" />
                <TextField type="date" size="small" label="from" value={s.irec_from}
                           onChange={(e)=>this.setState({ irec_from:e.target.value })}
                           sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
                <TextField type="date" size="small" label="to" value={s.irec_to}
                           onChange={(e)=>this.setState({ irec_to:e.target.value })}
                           sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
                <Button size="small" onClick={() => this.setState({
                  irec_from: firstDayOfMonth(s.selectedMonth),
                  irec_to: lastDayOfMonth(s.selectedMonth),
                })}>Use period</Button>
                <TextField size="small" label="timeout" value={s.irec_timeout}
                           onChange={(e)=>this.setState({ irec_timeout:e.target.value })}
                           sx={{ width: 100 }} />
                <FormControlLabel
                  control={<Checkbox checked={s.irec_verify} onChange={(e)=>this.setState({ irec_verify:e.target.checked })} />}
                  label="verify" />
                <FormControlLabel
                  control={<Checkbox checked={s.irec_keep} onChange={(e)=>this.setState({ irec_keep:e.target.checked })} />}
                  label="keep" />
              </this.ActionRow>

              <this.ActionRow
                label="Movements"
                runButton={
                  <Button size="small" variant="contained" startIcon={<PlayArrowIcon/>}
                          disabled={s.running} onClick={this.runMovements}>
                    Run
                  </Button>
                }
              >
                <TextField 
                  size="small" label="org_name" value={s.imov_org}
                  onChange={(e)=>this.setState({ imov_org:e.target.value })}
                  sx={{
                    ...compactFieldSx,
                    width: {
                      xs: '100%',
                      sm: 220,
                    },
                  }}
                />
                <TextField 
                  size="small" label="acct_schema" value={s.imov_schema}
                  onChange={(e)=>this.setState({ imov_schema:e.target.value })}
                  sx={{
                    ...compactFieldSx,
                    width: {
                      xs: '100%',
                      sm: 220,
                    },
                  }}
                />
                <FormControlLabel
                  control={<Checkbox checked={s.imov_no_close_default} onChange={(e)=>this.setState({ imov_no_close_default:e.target.checked })} />}
                  label="no_close_default" />
                <TextField type="date" size="small" label="from" value={s.imov_from}
                           onChange={(e)=>this.setState({ imov_from:e.target.value })}
                           sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
                <TextField type="date" size="small" label="to" value={s.imov_to}
                           onChange={(e)=>this.setState({ imov_to:e.target.value })}
                           sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
                <Button size="small" onClick={() => this.setState({
                  imov_from: firstDayOfMonth(s.selectedMonth),
                  imov_to: lastDayOfMonth(s.selectedMonth),
                })}>Use period</Button>
                <TextField size="small" label="timeout" value={s.imov_timeout}
                           onChange={(e)=>this.setState({ imov_timeout:e.target.value })}
                           sx={{ width: 100 }} />
                <FormControlLabel
                  control={<Checkbox checked={s.imov_verify} onChange={(e)=>this.setState({ imov_verify:e.target.checked })} />}
                  label="verify" />
                <FormControlLabel
                  control={<Checkbox checked={s.imov_keep} onChange={(e)=>this.setState({ imov_keep:e.target.checked })} />}
                  label="keep" />
                <TextField 
                  size="small" label="locations (csv)" value={s.imov_locations}
                  onChange={(e)=>this.setState({ imov_locations:e.target.value })}
                  sx={{
                    ...compactFieldSx,
                    width: {
                      xs: '100%',
                      sm: 220,
                    },
                  }}
                />
              </this.ActionRow>

              <this.ActionRow
                label="Inventory CSV"
                runButton={
                  <Button size="small" variant="contained" startIcon={<UploadFileIcon/>}
                          disabled={s.running} onClick={this.pickIINV}>
                    Upload & Run
                  </Button>
                }
              >
                <FormControlLabel
                  control={<Checkbox checked={s.iinv_replace} onChange={(e)=>this.setState({ iinv_replace:e.target.checked })} />}
                  label="replace" />
                <FormControlLabel
                  control={<Checkbox checked={s.iinv_stamp} onChange={(e)=>this.setState({ iinv_stamp:e.target.checked })} />}
                  label="stamp_central" />
                <TextField 
                  size="small" label="central_label" value={s.iinv_central_label}
                  onChange={(e)=>this.setState({ iinv_central_label:e.target.value })}
                  sx={{
                    ...compactFieldSx,
                    width: {
                      xs: '100%',
                      sm: 220,
                    },
                  }}
                />
                <Button size="small" variant="outlined" color="success" onClick={this.downloadInventoryTemplateLocal}>CSV template</Button>
                <input type="file" ref={this.fileRefIINV} style={{ display:'none' }}
                       accept=".csv, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                       onChange={this.onPickedIINV} />
              </this.ActionRow>

              <this.ActionRow
                label="Month Close"
                runButton={
                  <Button size="small" variant="contained" startIcon={<PlayArrowIcon/>}
                          disabled={s.running} onClick={this.runMonthClose}>
                    Run
                  </Button>
                }
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                  <Autocomplete
                    options={this.locationOptions}
                    getOptionLabel={(o)=>o.label}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id} 
                    value={this.locationOptions.find(o=>o.id===s.selectedLocId) || null}
                    onChange={(_, val)=>this.setState({
                      selectedLocId: val?.id || '',
                      selectedLocName: val?.label || '',
                    })}
                    renderInput={(params)=><TextField {...params} label="Location" size="small" sx={{ minWidth: 280 }} />}
                  />
                  <TextField
                    type="month" size="small" label="Period"
                    value={s.selectedMonth}
                    onChange={(e)=>{
                      const v = e.target.value;
                      this.setState({ selectedMonth: v }, ()=>{
                        this.precheckAllToState(v);
                        if (this.state.selectedLocId) this.autoPrecheck(this.state.selectedLocId, v);
                      });
                    }}
                    sx={{ width: 160 }} InputLabelProps={{ shrink: true }}
                  />
                  <Tooltip title="Fill From/To using selected month">
                    <Button size="small" onClick={()=>this.setState({
                      imov_from: firstDayOfMonth(s.selectedMonth),
                      imov_to: lastDayOfMonth(s.selectedMonth),
                      irec_from: firstDayOfMonth(s.selectedMonth),
                      irec_to: lastDayOfMonth(s.selectedMonth),
                    })}>
                      Autofill dates
                    </Button>
                  </Tooltip>
                </Stack>

                <FormControlLabel
                  control={<Checkbox checked={s.mc_overwrite} onChange={(e)=>this.setState({ mc_overwrite:e.target.checked })} />}
                  label="overwrite" />
                <FormControlLabel
                  control={<Checkbox checked={s.mc_dry_run} onChange={(e)=>this.setState({ mc_dry_run:e.target.checked })} />}
                  label="dry_run" />
              </this.ActionRow>
            
              <this.ActionRow
                label="Save COGS History"
                runButton={
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlayArrowIcon/>}
                    disabled={s.running}
                    onClick={this.runApplyClose}
                  >
                    Run
                  </Button>
                }
              >
                <Autocomplete
                  options={this.locationOptions}
                  getOptionLabel={(o)=>o.label}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  value={this.locationOptions.find(o=>o.id===s.selectedLocId) || null}
                  onChange={(_, val)=>this.setState({
                    selectedLocId: val?.id || '',
                    selectedLocName: val?.label || '',
                  })}
                  renderInput={(params)=>(
                    <TextField
                      {...params}
                      label="Location / All"
                      size="small"
                      sx={{
                        ...compactFieldSx,
                        minWidth: {
                          xs: '100%',
                          sm: 220,
                        },
                      }}
                    />
                  )}
                />
                <TextField
                  type="month"
                  size="small"
                  label="Period (optional)"
                  value={s.selectedMonth}
                  onChange={(e)=>this.setState({ selectedMonth:e.target.value })}
                  sx={{
                    ...compactFieldSx,
                    width: {
                      xs: 'calc(50% - 6px)',
                      sm: 150,
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={s.ach_all_unsynced}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={s.ach_all_unsynced}
                      onChange={(e)=>this.setState({ ach_all_unsynced:e.target.checked })}
                    />
                  }
                  label="All unsynced"
                />
              </this.ActionRow>
              <this.ActionRow
                label="Internal Use CSV"
                runButton={
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<UploadFileIcon/>}
                    disabled={s.running}
                    onClick={this.pickIIUSE}
                  >
                    Upload & Run
                  </Button>
                }
              >
                <Button size="small" variant="outlined" color="success" onClick={this.downloadInternalUseTemplateLocal}>CSV template</Button>
                <input
                  type="file"
                  ref={this.fileRefIIUSE}
                  style={{ display:'none' }}
                  accept=".csv, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={this.onPickedIIUSE}
                />
              </this.ActionRow>
              <this.ActionRow
                label="Internal Receipt CSV"
                runButton={
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<UploadFileIcon/>}
                    disabled={s.running}
                    onClick={this.pickIMR}
                  >
                    Upload & Run
                  </Button>
                }
              >
                <Button size="small" variant="outlined" color="success" onClick={this.downloadMaterialReceiptTemplateLocal}>
                  CSV template
                </Button>

                <input
                  type="file"
                  ref={this.fileRefIMR}
                  style={{ display:'none' }}
                  accept=".csv, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={this.onPickedIMR}
                />
              </this.ActionRow>

            </Paper>
          </Grid>

          {/* BOTTOM: Execution log */}
          <Grid
            item
            xs={12}
            sx={{
              height: {
                xs: 0,
                md: 130,
              },
              minHeight: 0,
              overflow: 'hidden',
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          >
            <Paper sx={logPaperSx}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  mb: 0.25,
                  minHeight: 22,
                  '& .MuiButton-root': {
                    height: 24,
                    minWidth: 48,
                    fontSize: '0.7rem',
                    px: 0.75,
                  },
                }}
              >
                <small style={{ opacity:.8 }}>Execution Log</small>
                <Button size="small" onClick={()=>this.setState({ log: [] })}>Clear</Button>
              </Stack>
              <Box sx={logBoxSx}>
                {this.state.log.length === 0 && (
                  <div style={{ padding: 8, color:'#9fb0ff' }}>
                    No logs yet. Run a command to see output here.
                  </div>
                )}
                {this.state.log.map((L, idx) => (
                  <div key={idx} style={{ marginBottom: 14 }}>
                    <div>
                      <span style={{ color:'#9fb0ff' }}>
                        [{L.ts}] {L.ok ? '✔' : '✖'} {L.cmd}
                      </span>
                    </div>
                    <div style={{ opacity:.9, marginTop: 4 }}>REQ:</div>
                    <pre style={{ margin: 0, whiteSpace:'pre-wrap' }}>{safeStringify(L.req)}</pre>
                    <div style={{ opacity:.9, marginTop: 8 }}>OUTPUT:</div>
                    <pre style={{ margin: 0, whiteSpace:'pre-wrap' }}>
                      {L.lines.map((ln, i) => {
                        const color =
                          ln.level === 'error' ? '#ff6b6b' :
                          ln.level === 'warning' ? '#ffb74d' :
                          ln.level === 'debug' ? '#90caf9' :
                          ln.level === 'stats' ? '#a5d6a7' : '#e6eaff';
                        return <span key={i} style={{ color }}>{ln.text + '\n'}</span>;
                      })}
                    </pre>
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ cursor:'pointer' }}>Full response JSON</summary>
                      <pre style={{ margin: 0, whiteSpace:'pre-wrap' }}>{safeStringify(L.res)}</pre>
                    </details>
                  </div>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Modal: Precheck for ALL → pick concrete location */}
        <Dialog
          open={this.state.precheckDialogOpen}
          onClose={() => {}}
          disableEscapeKeyDown
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Select location to import opening snapshot</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
              We ran a precheck for <b>{this.state.selectedMonth}</b>. Choose a location:
            </Typography>
            {(() => {
              const list = this.state.precheckDialogList || [];
              const total = list.length;
              const withData = list.filter(r => (r.count || 0) > 0).length;
              const withDup  = list.filter(r => (r.count || 0) > 1).length;

              if (total === 0) return null;

              // choose severity based on situation
              const severity =
                withDup > 0 ? 'warning'
                : withData > 0 ? 'info'
                : 'success';

              return (
                <Alert severity={severity} sx={{ mb: 1.5 }}>
                  <AlertTitle>
                    {withDup > 0 ? 'Some locations have duplicate month closes'
                    : withData > 0 ? 'Some locations already have a month close'
                    : 'No existing month closes for this period'}
                  </AlertTitle>

                  {withDup > 0 && (
                    <>
                      {withDup} of {total} location(s) have <b>duplicates</b> for this month
                      and cannot be imported until resolved. They’re marked as <b>dup×N</b> in the list below.
                    </>
                  )}

                  {withDup === 0 && withData > 0 && (
                    <>
                      {withData} of {total} location(s) already have a close for this month.
                      They will be <b>skipped</b> unless you enable <b>overwrite</b>.
                      <br />
                      Tip: toggle the <b>overwrite</b> checkbox in the action row before uploading.
                    </>
                  )}

                  {withDup === 0 && withData === 0 && (
                    <>
                      All {total} location(s) are clear — importing will create new month closes for each.
                    </>
                  )}
                </Alert>
              );
            })()}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Existing</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.state.precheckDialogList.map(r => {
                  const dup = (r.count || 0) > 1;
                  const one = (r.count || 0) === 1;
                  return (
                    <TableRow key={r.location_id} hover>
                      <TableCell>{r.location_name}</TableCell>
                      <TableCell align="right">
                        {dup ? <Chip size="small" color="warning" label={`dup×${r.count}`} /> :
                         one ? <Chip size="small" label="exists" /> : <span>—</span>}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          disabled={dup}
                          onClick={() => {
                            this.setState({
                              precheckDialogOpen:false,
                              selectedLocId: r.location_id,
                              selectedLocName: r.location_name,
                            }, () => this.fileRefIOC.current?.click());
                          }}
                        >
                          Import here
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="caption" sx={{ display:'block', mt: 1.5, opacity: 0.7 }}>
              Locations with <b>dup×N</b> have multiple month closes for this period and are blocked until resolved.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>this.setState({ precheckDialogOpen:false })}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Jobs Modal (compact split) */}
        <Dialog
          open={this.state.jobsModalOpen}
          onClose={() => {
            // do nothing on backdrop / esc
          }}
          disableEscapeKeyDown
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Inventory jobs
            <Button size="small" sx={{ ml: 1 }} onClick={this.refreshJobsList}>Refresh</Button>
            <Tooltip title="Close">
              <IconButton
                aria-label="close"
                onClick={this.closeJobs}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              {/* Left: jobs list */}
              <Grid item xs={12} md={4}>
                <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Kind</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {this.state.jobsLoading ? (
                        <TableRow><TableCell colSpan={3}>Loading…</TableCell></TableRow>
                      ) : (this.state.jobs.length === 0 ? (
                        <TableRow><TableCell colSpan={3}>No jobs yet</TableCell></TableRow>
                      ) : this.state.jobs.map(j => {
                        const selected = this.state.jobSelected?.id === j.id;
                        return (
                          <TableRow
                            key={j.id}
                            hover
                            selected={selected}
                            onClick={() => this.selectJob(j.id)}
                            sx={{ cursor:'pointer' }}
                          >
                            <TableCell>{j.id}</TableCell>
                            <TableCell>{j.kind}</TableCell>
                            <TableCell>
                              <Chip size="small" label={j.status}
                                color={
                                  j.status === 'SUCCESS' ? 'success' :
                                  j.status === 'FAILED' ? 'error' :
                                  j.status === 'CANCELLED' ? 'warning' : 'default'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      }))}
                    </TableBody>
                  </Table>
                </Box>
              </Grid>

              {/* Right: selected job detail + logs */}
              <Grid item xs={12} md={8}>
                {!this.state.jobSelected ? (
                  <Typography variant="body2" sx={{ opacity: .8 }}>Select a job to view details</Typography>
                ) : (
                  <>
                    {(() => {
                      const b = this.jobBanner(this.state.jobSelected);
                      return (
                        <Box sx={{ mb: 1.5 }}>
                          <Alert severity={b.severity} variant="outlined">
                            <strong>{b.title}</strong>
                            {b.text ? <span style={{ marginLeft: 8 }}>{b.text}</span> : null}
                          </Alert>
                        </Box>
                      );
                    })()}

                    <Stack spacing={1} sx={{ mb: 1.25 }}>
                      {/* First line: ID, Kind, Status, Cancel */}
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip size="small" label={`ID: ${this.state.jobSelected.id}`} />
                        <Chip size="small" label={`Kind: ${this.state.jobSelected.kind}`} />
                        <Chip size="small" label={`Status: ${this.state.jobSelected.status}`} />

                        {this.state.jobSelected.cancel_requested && (
                          <Chip size="small" color="warning" label="Cancel requested" />
                        )}
                      </Stack>

                      {/* Second line: Started / Finished */}
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {this.state.jobSelected.started_at && (
                          <Chip
                            size="small"
                            label={`Started: ${new Date(this.state.jobSelected.started_at).toLocaleString()}`}
                            color="info"
                          />
                        )}

                        {this.state.jobSelected.finished_at && (
                          <Chip
                            size="small"
                            label={`Finished: ${new Date(this.state.jobSelected.finished_at).toLocaleString()}`}
                            color="success"
                          />
                        )}
                      </Stack>
                    </Stack>

                    <Box sx={{
                      maxHeight: 420, overflow: 'auto',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      fontSize: 12, lineHeight: 1.5, background: '#0b1020', color: '#e6eaff',
                      p: 1, borderRadius: 1, textAlign: 'left', border: '1px solid #19224B'
                    }}>
                      <JobLogViewer job={this.state.jobSelected} />
                      {['RUNNING','PENDING'].includes(this.state.jobSelected.status) && (
                        <div style={{ opacity:.8, marginTop:6, display:'flex', alignItems:'center' }}>
                          <CircularProgress size={14} style={{ marginRight: 6 }} /> streaming…
                        </div>
                      )}
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ pr: 2.5, gap: 1.5 }}>
            <Button
              onClick={this.downloadJobLogs}
              disabled={!this.state.jobSelected}
              variant="outlined"
            >
              Download logs
            </Button>
            <Button
              onClick={this.cancelJob}
              color="warning"
              disabled={
                !this.state.jobSelected ||
                !['RUNNING','PENDING'].includes(this.state.jobSelected.status) ||
                this.state.jobSelected.cancel_requested
              }
            >
              Cancel job
            </Button>
            {/* <Button onClick={this.closeJobs}>Close</Button> */}
          </DialogActions>
        </Dialog>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={this.fileRefIOC}
          style={{ display:'none' }}
          accept=".csv, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={this.onPickedIOC}
        />
        <input
          type="file"
          ref={this.fileRefIINV}
          style={{ display:'none' }}
          accept=".csv, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={this.onPickedIINV}
        />

        <Snackbar open={this.state.snack.open} autoHideDuration={4000} onClose={this.closeSnack}
          anchorOrigin={{ vertical:'bottom', horizontal:'center' }}>
          <Alert onClose={this.closeSnack} severity={this.state.snack.severity} sx={{ width:'100%' }}>
            {this.state.snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}